// src/virement.js — combien garder sur BNP, combien virer vers Revolut.
//
// BNP encaisse tout (Square, espèces) et porte les prélèvements récurrents.
// Revolut n'encaisse rien : il ne vit que des virements et paie les salaires du
// 3 et les fournisseurs.
//
// La question utile n'est pas « quand virer » mais « combien doit rester sur
// BNP » : ce compte doit couvrir ses propres prélèvements jusqu'à la fin du
// mois. Ce qui dépasse est virable, le reste ne l'est pas. Un calendrier de
// virements fixes multipliait les gestes sans jamais répondre à ça.
//
// D'où un seul virement programmé — celui des salaires — et des appoints
// seulement quand Revolut tombe à sec.
//
// ⚠️ Module volontairement en CommonJS : il est importé par le dashboard ET
// exécuté tel quel par `node scripts/recette-virement.js`. CRA interdit
// d'importer depuis lib/, d'où sa présence dans src/.

// Marge d'imprévu conservée sur BNP en plus des prélèvements à venir (casse,
// réparation). Les points bas réels ont été 2 512 € le 3/09 et 2 691 € le 20/07.
const PLANCHER_BNP = 1500;

// Ce que les salaires coûtent, payés depuis Revolut le 3.
const SALAIRES_ATTENDUS = 4500;

// Sous ce solde, Revolut ne peut plus honorer une commande fournisseur : on
// remet au niveau d'un fonds de roulement.
const SEUIL_REVOLUT = 800;
const CIBLE_ROULEMENT = 1500;

// Unique échéance programmée : amener Revolut à hauteur des salaires du 3.
// La fenêtre enjambe la fin de mois.
const FENETRE_SALAIRES = { du: 28, au: 2, cible: 5000, motif: 'salaires du 3' };

const eur = (n) => Math.round(n);
const dansFenetre = (jour, f) => (f.du <= f.au ? jour >= f.du && jour <= f.au : jour >= f.du || jour <= f.au);

/**
 * @param {object} o
 * @param {number} o.jour          jour du mois (1-31)
 * @param {number} o.soldeBNP      solde BNP Café
 * @param {number} o.soldeRevolut  solde Revolut Café
 * @param {Array}  o.echeances     [{ jour, montant, label, compte: 'BNP'|'Revolut', paye: boolean }]
 * @returns {object} voir les champs ci-dessous
 */
function conseilVirement({ jour, soldeBNP, soldeRevolut, echeances = [] }) {
  // Tout prélèvement BNP non encore constaté reste dû, y compris si son jour est
  // passé : une URSSAF en retard sort quand même du compte.
  const aVenir = echeances.filter(e => e.compte !== 'Revolut' && !e.paye && Number.isFinite(e.montant));
  const resteCharges = eur(aVenir.reduce((s, e) => s + e.montant, 0));
  const aGarder = resteCharges + PLANCHER_BNP;
  const virable = Math.max(0, eur(soldeBNP) - aGarder);

  const fenetreSalaires = dansFenetre(jour, FENETRE_SALAIRES);
  const cible = fenetreSalaires ? FENETRE_SALAIRES.cible
    : eur(soldeRevolut) < SEUIL_REVOLUT ? CIBLE_ROULEMENT
    : null;
  const motif = fenetreSalaires ? FENETRE_SALAIRES.motif
    : cible ? 'Revolut à sec — fonds de roulement fournisseurs'
    : null;

  const manque  = cible ? Math.max(0, cible - eur(soldeRevolut)) : 0;
  const montant = Math.min(manque, virable);

  // Revolut ne pourra pas payer les salaires du 3, et BNP n'a pas la marge :
  // c'est le seul moment où ça se voit encore à temps.
  const alerte = fenetreSalaires && eur(soldeRevolut) + montant < SALAIRES_ATTENDUS
    ? `Revolut couvrirait ${eur(soldeRevolut) + montant} € face à ~${SALAIRES_ATTENDUS} € de salaires le 3`
    : null;

  return {
    resteCharges,          // prélèvements BNP encore à sortir ce mois
    aGarder,               // resteCharges + plancher d'imprévu
    plancher: PLANCHER_BNP,
    virable,               // ce que BNP peut lâcher sans se mettre en risque
    action: montant > 0 ? 'virer' : 'attendre',
    montant,
    motif,
    cible,
    manque,
    plafonne: montant < manque,
    fenetreSalaires,
    alerte,
    lignes: aVenir.slice().sort((a, b) => a.jour - b.jour),
  };
}

module.exports = {
  conseilVirement,
  PLANCHER_BNP,
  SALAIRES_ATTENDUS,
  SEUIL_REVOLUT,
  CIBLE_ROULEMENT,
  FENETRE_SALAIRES,
};
